import { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { GqlCustomNavigationItem, CustomNavigationItemType, GqlCustomNavigation } from 'types/graphql';

import { useCustomNavigationData } from './use-custom-navigation-data';
import { useNavigationItemDragDrop, getItemsAtPath, updateItemsAtPath } from './use-navigation-item-drag-drop';

type UseCustomNavigationFormProps = {
  initialNavigation?: GqlCustomNavigation | null;
};

type UseCustomNavigationFormReturn = {
  currentPath: string[];
  currentItems: GqlCustomNavigationItem[];
  isDirty: boolean;
  isEnabled: boolean;
  isLoading: boolean;
  isSaving: boolean;
  navigationItems: GqlCustomNavigationItem[];
  navigationLabel: string;
  handleAddItem: (item: GqlCustomNavigationItem) => void;
  handleRemoveItem: (itemId: string) => void;
  handleMoveItem: (dragIndex: number, hoverIndex: number) => void;
  handleEditItem: (itemId: string, updates: Partial<GqlCustomNavigationItem>) => void;
  handleEnabledChange: (enabled: boolean) => void;
  handleLabelChange: (label: string) => void;
  handleSave: (targetDispensaryIds?: string[]) => Promise<void>;
  handleReset: () => void;
  handleNavigateToSection: (sectionId: string) => void;
  handleNavigateToPath: (path: string[]) => void;
  createNewItem: (type: CustomNavigationItemType) => GqlCustomNavigationItem;
  refetch: () => void;
};

export function useCustomNavigationForm({
  initialNavigation,
}: UseCustomNavigationFormProps = {}): UseCustomNavigationFormReturn {
  const { customNavigation, loading: dataLoading, saving, saveNavigation, refetch } = useCustomNavigationData();
  const [navigationItems, setNavigationItems] = useState<GqlCustomNavigationItem[]>([]);
  const [navigationLabel, setNavigationLabel] = useState<string>('Navigation');
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [isDirty, setIsDirty] = useState(false);
  const [currentPath, setCurrentPath] = useState<string[]>([]);

  useEffect(() => {
    if (isDirty) {
      return;
    }

    const navigation = initialNavigation ?? customNavigation;
    if (navigation?.items) {
      setNavigationItems(navigation.items);
    }
    if (navigation?.label) {
      setNavigationLabel(navigation.label);
    }
    if (navigation?.enabled !== undefined) {
      setIsEnabled(navigation.enabled);
    }
  }, [initialNavigation, customNavigation, isDirty]);

  const currentItems = useMemo(() => getItemsAtPath(navigationItems, currentPath), [navigationItems, currentPath]);

  const { handleMoveItem, handleRemoveItem } = useNavigationItemDragDrop({
    currentPath,
    navigationItems,
    setCurrentPath,
    setNavigationItems,
    setIsDirty,
  });

  const handleAddItem = useCallback(
    (item: GqlCustomNavigationItem) => {
      setNavigationItems((prev) => {
        const currentLevelItems = getItemsAtPath(prev, currentPath);
        const newItem = { ...item, position: currentLevelItems.length };

        return updateItemsAtPath(prev, currentPath, (items) => [...items, newItem]);
      });
      setIsDirty(true);
    },
    [currentPath]
  );

  const handleEditItem = useCallback((itemId: string, updates: Partial<GqlCustomNavigationItem>) => {
    const updateInTree = (items: GqlCustomNavigationItem[]): GqlCustomNavigationItem[] =>
      items.map((item) => {
        if (item.id === itemId) {
          return { ...item, ...updates };
        }
        return {
          ...item,
          items: updateInTree(item.items),
        };
      });
    setNavigationItems((prev) => updateInTree(prev));
    setIsDirty(true);
  }, []);

  const handleLabelChange = useCallback((label: string) => {
    setNavigationLabel(label);
    setIsDirty(true);
  }, []);

  const handleEnabledChange = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(
    async (targetDispensaryIds?: string[]) => {
      const result = await saveNavigation(navigationItems, navigationLabel, isEnabled, targetDispensaryIds);
      if (result) {
        setIsDirty(false);
      }
    },
    [navigationItems, navigationLabel, isEnabled, saveNavigation]
  );

  const handleReset = useCallback(() => {
    const navigation = initialNavigation ?? customNavigation;
    if (navigation?.items) {
      setNavigationItems(navigation.items);
    } else {
      setNavigationItems([]);
    }
    if (navigation?.label) {
      setNavigationLabel(navigation.label);
    } else {
      setNavigationLabel('Navigation');
    }
    setIsEnabled(navigation?.enabled ?? true);
    setIsDirty(false);
    setCurrentPath([]);
  }, [initialNavigation, customNavigation]);

  const handleNavigateToSection = useCallback((sectionId: string) => {
    setCurrentPath((prev) => [...prev, sectionId]);
  }, []);

  const handleNavigateToPath = useCallback((path: string[]) => {
    setCurrentPath(path);
  }, []);

  const createNewItem = useCallback(
    (navigationType: CustomNavigationItemType): GqlCustomNavigationItem => ({
      id: uuid(),
      items: [],
      label: '',
      navigationType,
      position: currentItems.length,
    }),
    [currentItems.length]
  );

  return {
    currentPath,
    currentItems,
    navigationItems,
    navigationLabel,
    isDirty,
    isEnabled,
    isLoading: dataLoading,
    isSaving: saving,
    handleAddItem,
    handleRemoveItem,
    handleMoveItem,
    handleEditItem,
    handleEnabledChange,
    handleLabelChange,
    handleSave,
    handleReset,
    handleNavigateToSection,
    handleNavigateToPath,
    createNewItem,
    refetch,
  };
}
